import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PortableText } from '@portabletext/react';
import { getNewsArticleBySlug, getOptimizedImageUrl } from '../sanity/client';
import SocialShare from '../components/SocialShare';
import './NewsArticlePage.css';

const portableTextComponents = {
  block: {
    h2: ({ children }) => <h2 className="article-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="article-h3">{children}</h3>,
    blockquote: ({ children }) => <blockquote className="article-quote">{children}</blockquote>,
    normal: ({ children }) => <p className="article-paragraph">{children}</p>,
  },
  marks: {
    link: ({ children, value }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" className="article-link">
        {children}
      </a>
    ),
  },
};

const NewsArticlePage = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Get localized title and content based on current language
  const getLocalizedField = (fieldName) => {
    if (!article) return '';
    const lang = i18n.language === 'it' ? 'It' : 'En';
    return article[`${fieldName}${lang}`] || article[`${fieldName}En`];
  };

  const title = getLocalizedField('title');
  const content = getLocalizedField('content');

  useEffect(() => {
    if (article) {
      document.title = `${getLocalizedField('title')} - Magicomms`;
    }
  }, [article, i18n.language]);

  useEffect(() => {
    // Scroll to top when component mounts or slug changes
    window.scrollTo(0, 0);
    
    const fetchArticle = async () => {
      try {
        const data = await getNewsArticleBySlug(slug);
        if (!data) {
          setError('Article not found');
        } else {
          setArticle(data);
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="news-article-page">
        <div className="article-container">
          <div className="article-loading">
            <div className="skeleton-back"></div>
            <div className="skeleton-date-large"></div>
            <div className="skeleton-title-large"></div>
            <div className="skeleton-image-large"></div>
            <div className="skeleton-content"></div>
            <div className="skeleton-content"></div>
            <div className="skeleton-content short"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-article-page">
        <div className="article-container">
          <Link to="/news" className="article-back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to News
          </Link>
          <p className="article-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-article-page">
      <div className="article-container">
        <article className="article-content">
          <header className="article-header">
            <div className="article-header-top">
              <Link to="/news" className="article-back">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                {t('news.backToNews')}
              </Link>
              <time className="article-date">{formatDate(article.publishedDate)}</time>
            </div>
            <h1 className="article-title">{title}</h1>
          </header>

          {article.mainImage?.asset?.url && (
            <div className="article-main-image">
              <img 
                src={getOptimizedImageUrl(article.mainImage, { width: 1200, quality: 90 })} 
                alt={title}
                onClick={() => setLightboxImage(getOptimizedImageUrl(article.mainImage, { width: 1920, quality: 95 }))}
              />
            </div>
          )}

          {content && (
            <div className="article-body">
              <PortableText value={content} components={portableTextComponents} />
            </div>
          )}

          {article.additionalImages && article.additionalImages.length > 0 && (
            <div className="article-gallery">
              <h3 className="gallery-title">{t('news.gallery')}</h3>
              <div className="gallery-grid">
                {article.additionalImages.map((img, index) => (
                  <div 
                    key={index} 
                    className="gallery-item"
                    onClick={() => setLightboxImage(getOptimizedImageUrl(img, { width: 1920, quality: 95 }))}
                  >
                    <img src={getOptimizedImageUrl(img, { width: 800, quality: 85 })} alt={`${title} - Image ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <SocialShare 
            url={window.location.href}
            title={title}
            description={title}
          />
        </article>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <img src={lightboxImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default NewsArticlePage;
